import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Package, Plus, Trash2, History, BarChart3, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MaterialsInward() {
  const [materials, setMaterials] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [otherMaterials, setOtherMaterials] = useState([]);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    supplier_name: '',
    m_sand_dust: 0,
    m_sand_waste: 0,
    fly_ash_raw: 0,
    cement: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [materialsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/materials-inward`, { headers }),
        axios.get(`${API}/materials-summary`, { headers })
      ]);
      setMaterials(materialsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const addOtherMaterial = () => {
    setOtherMaterials([...otherMaterials, { name: '', quantity: '' }]);
  };

  const updateOtherMaterial = (index, field, value) => {
    const updated = [...otherMaterials];
    updated[index][field] = value;
    setOtherMaterials(updated);
  };

  const removeOtherMaterial = (index) => {
    setOtherMaterials(otherMaterials.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name) {
      toast.error('Please enter supplier name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        other_materials: otherMaterials.filter(m => m.name && m.quantity)
      };
      
      await axios.post(`${API}/materials-inward`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Material entry recorded successfully');
      setFormData({
        date: new Date().toISOString().split('T')[0],
        supplier_name: '',
        m_sand_dust: 0,
        m_sand_waste: 0,
        fly_ash_raw: 0,
        cement: 0,
        notes: ''
      });
      setOtherMaterials([]);
      setShowAddDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record material entry');
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this entry? Cement stock will be adjusted.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/materials-inward/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material entry deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  return (
    <div data-testid="materials-inward-page" className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight">
            <Package className="inline-block mr-3 h-10 w-10 text-brick-600" />
            Materials Inward
          </h1>
          <p className="font-inter text-concrete-600 mt-2">Track incoming raw materials and supplies</p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-brick-600 hover:bg-brick-500 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Material Entry
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-concrete-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-concrete-500 uppercase tracking-wider">M Sand Dust</p>
            <p className="text-2xl font-bold text-concrete-900 mt-1">{summary.m_sand_dust.toFixed(2)}</p>
            <p className="text-xs text-concrete-500">Tonnes</p>
          </div>
          <div className="bg-white border border-concrete-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-concrete-500 uppercase tracking-wider">M Sand Waste</p>
            <p className="text-2xl font-bold text-concrete-900 mt-1">{summary.m_sand_waste.toFixed(2)}</p>
            <p className="text-xs text-concrete-500">Tonnes</p>
          </div>
          <div className="bg-white border border-concrete-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-concrete-500 uppercase tracking-wider">Fly Ash (Raw)</p>
            <p className="text-2xl font-bold text-concrete-900 mt-1">{summary.fly_ash_raw.toFixed(2)}</p>
            <p className="text-xs text-concrete-500">Tonnes</p>
          </div>
          <div className="bg-white border border-concrete-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-concrete-500 uppercase tracking-wider">Cement</p>
            <p className="text-2xl font-bold text-brick-600 mt-1">{summary.cement.toLocaleString()}</p>
            <p className="text-xs text-concrete-500">KGs (Added to Stock)</p>
          </div>
          <div className="bg-white border border-concrete-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs font-semibold text-concrete-500 uppercase tracking-wider">Total Entries</p>
            <p className="text-2xl font-bold text-concrete-900 mt-1">{summary.total_entries}</p>
            <p className="text-xs text-concrete-500">Records</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="history" className="w-full">
        <TabsList className="bg-white border border-concrete-200">
          <TabsTrigger value="history" className="font-manrope font-semibold">
            <History className="mr-2 h-4 w-4" />
            Entry History
          </TabsTrigger>
          <TabsTrigger value="summary" className="font-manrope font-semibold">
            <BarChart3 className="mr-2 h-4 w-4" />
            Material Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6">
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</th>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Supplier</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">M Sand Dust</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">M Sand Waste</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Cement</th>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Other</th>
                    <th className="px-4 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {materials.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center font-inter text-concrete-500">
                        No material entries recorded yet
                      </td>
                    </tr>
                  ) : (
                    materials.map((material) => (
                      <tr key={material.id} className="hover:bg-concrete-50">
                        <td className="px-4 py-4 font-inter text-concrete-700">{material.date}</td>
                        <td className="px-4 py-4 font-inter font-medium text-concrete-900">{material.supplier_name}</td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">
                          {material.m_sand_dust > 0 ? `${material.m_sand_dust} T` : '-'}
                        </td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">
                          {material.m_sand_waste > 0 ? `${material.m_sand_waste} T` : '-'}
                        </td>
                        <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">
                          {material.fly_ash_raw > 0 ? `${material.fly_ash_raw} T` : '-'}
                        </td>
                        <td className="px-4 py-4 font-inter text-brick-600 font-semibold text-right tabular-nums">
                          {material.cement > 0 ? `${material.cement} kg` : '-'}
                        </td>
                        <td className="px-4 py-4 font-inter text-concrete-600 text-sm">
                          {material.other_materials && material.other_materials.length > 0 ? (
                            <div className="space-y-1">
                              {material.other_materials.map((om, idx) => (
                                <div key={idx} className="text-xs bg-concrete-100 px-2 py-1 rounded">
                                  {om.name}: {om.quantity}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Button
                            onClick={() => handleDelete(material.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="summary" className="mt-6">
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
            <h2 className="font-manrope font-bold text-xl mb-6">Material Summary by Supplier</h2>
            {materials.length === 0 ? (
              <p className="text-concrete-500 text-center py-8">No data available</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(
                  materials.reduce((acc, m) => {
                    if (!acc[m.supplier_name]) {
                      acc[m.supplier_name] = {
                        m_sand_dust: 0,
                        m_sand_waste: 0,
                        fly_ash_raw: 0,
                        cement: 0,
                        entries: 0
                      };
                    }
                    acc[m.supplier_name].m_sand_dust += m.m_sand_dust || 0;
                    acc[m.supplier_name].m_sand_waste += m.m_sand_waste || 0;
                    acc[m.supplier_name].fly_ash_raw += m.fly_ash_raw || 0;
                    acc[m.supplier_name].cement += m.cement || 0;
                    acc[m.supplier_name].entries += 1;
                    return acc;
                  }, {})
                ).map(([supplier, data]) => (
                  <div key={supplier} className="border border-concrete-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-concrete-900">{supplier}</h3>
                      <span className="text-xs text-concrete-500">{data.entries} entries</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-concrete-500">M Sand Dust</p>
                        <p className="font-semibold">{data.m_sand_dust.toFixed(2)} T</p>
                      </div>
                      <div>
                        <p className="text-concrete-500">M Sand Waste</p>
                        <p className="font-semibold">{data.m_sand_waste.toFixed(2)} T</p>
                      </div>
                      <div>
                        <p className="text-concrete-500">Fly Ash</p>
                        <p className="font-semibold">{data.fly_ash_raw.toFixed(2)} T</p>
                      </div>
                      <div>
                        <p className="text-concrete-500">Cement</p>
                        <p className="font-semibold text-brick-600">{data.cement.toLocaleString()} kg</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Material Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <Package className="h-5 w-5 text-brick-600" />
              Add Material Entry
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label>Supplier Name *</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                  className="mt-1"
                  placeholder="Enter supplier name"
                  required
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-concrete-800 mb-3">Materials (in Tonnes)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>M Sand Dust</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.m_sand_dust}
                    onChange={(e) => setFormData({ ...formData, m_sand_dust: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                    placeholder="Tonnes"
                  />
                </div>
                <div>
                  <Label>M Sand Waste</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.m_sand_waste}
                    onChange={(e) => setFormData({ ...formData, m_sand_waste: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                    placeholder="Tonnes"
                  />
                </div>
                <div>
                  <Label>Fly Ash (Raw)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.fly_ash_raw}
                    onChange={(e) => setFormData({ ...formData, fly_ash_raw: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                    placeholder="Tonnes"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-concrete-800 mb-3">Cement (in KGs)</h3>
              <div>
                <Label>Cement</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.cement}
                  onChange={(e) => setFormData({ ...formData, cement: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  placeholder="KGs"
                />
                <p className="text-xs text-concrete-500 mt-1">This will be added to cement stock automatically</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-concrete-800">Other Materials</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addOtherMaterial}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
              {otherMaterials.length === 0 ? (
                <p className="text-sm text-concrete-500">No other materials added</p>
              ) : (
                <div className="space-y-2">
                  {otherMaterials.map((material, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={material.name}
                        onChange={(e) => updateOtherMaterial(index, 'name', e.target.value)}
                        placeholder="Material name"
                        className="flex-1"
                      />
                      <Input
                        value={material.quantity}
                        onChange={(e) => updateOtherMaterial(index, 'quantity', e.target.value)}
                        placeholder="Quantity (e.g., 10 bags, 5 litres)"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOtherMaterial(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <Label>Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1"
                placeholder="Any additional notes"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-brick-600 hover:bg-brick-500">
                <Package className="mr-2 h-4 w-4" />
                Save Entry
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
